import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { Utils } from '../classes/Utils';
import { CashCloseMovementTotal, CashCloseTicketData, CashCloseTicketReference } from '../classes/CashCloseTicketBuilder';
import { CashCloseInfo, PaymentInfo, ReturnsInfo } from '../models/models';
import { Bank_Movement, Billing_Data, Cash_Count, Currency_Rate, Fund, Payment, Returns, Store, Withdrawal } from '../models/RestModels';
import { RestResponse, SearchObject } from './Rest';
import { RestService } from './rest.service';

interface ReturnsRequest extends Returns {
	order_id: number;
}

const INCOME_LEGEND: Record<string, string> = {
	'CASH-MXN': 'Efectivo',
	'CASH-USD': 'Dolares',
	'CREDIT_CARD-MXN': 'Tarjeta Crédito',
	'DEBIT_CARD-MXN': 'Tarjeta Débito',
	'CHECK-MXN': 'Cheque',
	'TRANSFER-MXN': 'Transferencia',
	'DIGITAL_WALLET-MXN': 'Monedero',
};

const EXPENSE_LEGEND: Record<string, string> = {
	'CASH': 'Efectivo',
	'CREDIT_CARD': 'Tarjeta Crédito',
	'DEBIT_CARD': 'Tarjeta Débito',
	'CHECK': 'Cheque',
	'TRANSFER': 'Transferencia',
	'DIGITAL_WALLET': 'Monedero',
	'PAYPAL': 'PayPal',
	'COUPON': 'Cupón',
	'RETURN_DISCOUNT': 'Devolución',
};

const DEFAULT_USD_RATE: Currency_Rate = {
	id: 0,
	rate: 19,
	store_id: 0,
	currency_id: 'USD'
};

function initialMovementsQtys(): Record<string, CashCloseMovementTotal> {
	return {
		'Efectivo': { total: 0, currency_id: 'MXN', equivalencia: 0 },
		'Dolares': { total: 0, currency_id: 'USD', equivalencia: 0 },
		'Tarjeta Crédito': { total: 0, currency_id: 'MXN', equivalencia: 0 },
		'Tarjeta Débito': { total: 0, currency_id: 'MXN', equivalencia: 0 },
		Transferencia: { total: 0, currency_id: 'MXN', equivalencia: 0 },
		Cheque: { total: 0, currency_id: 'MXN', equivalencia: 0 }
	};
}

export interface CashCloseReportInputs {
	cash_close_info: CashCloseInfo;
	payment_info_list: PaymentInfo[];
	returns_info_list: ReturnsInfo[];
	billing_data: Billing_Data | null;
	store: Store;
	current_date: Date;
}

export interface CashCloseComputedTotals {
	total_sales: number;
	total_credit: number;
	total_order_discount: number;
	total_item_discount: number;
	item_total: number;
	currencies_total: number;
	total_income_sales: number;
	total_income_other: number;
	total_order_amount_paid: number;
	total_funds: number;
	total_expenses: number;
	total_withdrawals: number;
	withdrawals: Withdrawal[];
	expenses_paymens: Payment[];
	movements_qtys: Record<string, CashCloseMovementTotal>;
	expense_movements_qtys: Record<string, CashCloseMovementTotal>;
	currency_rate: Currency_Rate;
	total_returns: number;
	total_change_amount: number;
	payments_with_change: Payment[];
	cash_count_mxn_bills: Cash_Count[];
	cash_count_usd_bills: Cash_Count[];
	cash_count_mxn_coins: Cash_Count[];
	cash_count_usd_coins: Cash_Count[];
	total_in_caja: number;
	diferencia_caja: number;
	show_negative_warning: boolean;
}

export interface CashCloseReport {
	inputs: CashCloseReportInputs;
	totals: CashCloseComputedTotals;
	ticket_data: CashCloseTicketData;
}

export function getAmountPaidForOrder(payment_info_list: PaymentInfo[], order_id: number): number {
	let amount = 0;

	for (let payment_info of payment_info_list) {
		for (let bm of payment_info.movements) {
			for (let bmo of bm.bank_movement_orders) {
				if (bmo.order_id == order_id) {
					amount += bmo.amount;
				}
			}
		}
	}
	return amount;
}

export function groupCashCountReferences(cash_counts: Cash_Count[] | undefined): CashCloseTicketReference[] {
	if (!cash_counts?.length) return [];

	let references: Record<string, { total: number; currency_id: string }> = {};

	for (let item of cash_counts) {
		if (!item.only_reference) continue;

		if (!references[item.type]) {
			references[item.type] = {
				total: 0,
				currency_id: item.currency_id
			};
		}

		references[item.type].total += item.denomination * item.quantity;
	}

	return Object.entries(references).map(([type, data]) =>
	({
		type,
		...data
	}));
}

export function sumExpenseMovements(expense_movements_qtys: Record<string, CashCloseMovementTotal>): number {
	return Object.keys(expense_movements_qtys || {}).reduce((sum, key) => {
		return sum + (expense_movements_qtys[key].total || 0);
	}, 0);
}

/**
 * Computes every total shown by the cashclose2 receipt.
 * Formulas moved verbatim from PrintCashClosing2Component so the browser
 * receipt and the USB receipt always share a single source of truth.
 */
export function computeCashCloseTotals(inputs: Pick<CashCloseReportInputs, 'cash_close_info' | 'payment_info_list' | 'returns_info_list'>): CashCloseComputedTotals {
	const cash_close_info = inputs.cash_close_info;
	const payment_info_list = inputs.payment_info_list;

	let cash_count_mxn_bills: Cash_Count[] = [];
	let cash_count_usd_bills: Cash_Count[] = [];
	let cash_count_mxn_coins: Cash_Count[] = [];
	let cash_count_usd_coins: Cash_Count[] = [];

	if (cash_close_info.cash_counts?.length) {
		let entries = cash_close_info.cash_counts;

		cash_count_mxn_bills = entries.filter(x => x.currency_id === 'MXN' && x.type === 'BILL');
		cash_count_usd_bills = entries.filter(x => x.currency_id === 'USD' && x.type === 'BILL');
		cash_count_mxn_coins = entries.filter(x => x.currency_id === 'MXN' && x.type === 'COIN');
		cash_count_usd_coins = entries.filter(x => x.currency_id === 'USD' && x.type === 'COIN');
	}

	let total_sales = 0;
	let total_credit = 0;
	let total_order_discount = 0;
	let total_item_discount = 0;

	let item_total = 0;
	let total_expenses = 0;
	let total_withdrawals = 0;

	let total_income_sales = 0;
	let total_income_other = 0;
	let total_order_amount_paid = 0;
	let total_change_amount = 0;
	let payments_with_change: Payment[] = [];
	let expense_movements_qtys: Record<string, CashCloseMovementTotal> = {};
	let movements_qtys = initialMovementsQtys();
	let currencies_total = 0;

	let total_returns = inputs.returns_info_list.reduce((sum, ret) => sum + ret.returns.total, 0);

	let tmp_payments: Payment[] = [];

	let currency_rate = cash_close_info.currency_rates.find(i => i.currency_id == 'USD' && i.store_id == cash_close_info.user.store_id);

	if (!currency_rate)
		currency_rate = { ...DEFAULT_USD_RATE };

	cash_close_info.payments.forEach((payment) => {
		if (payment.type == 'income') {
			let change = payment.change_amount || 0;
			let net_amount = payment.payment_amount;//- change;

			if (change > 0) {
				total_change_amount += change;
				payments_with_change.push(payment);
			}

			if (payment.tag == 'SALE') {
				total_income_sales += net_amount;
			}
			else {
				total_income_other += net_amount;
			}
		}
		else {
			tmp_payments.push(payment);
		}
	});

	let expenses_paymens = tmp_payments;

	let withdrawals = cash_close_info.withdrawals || [];
	total_withdrawals = withdrawals.reduce((sum: number, w: Withdrawal) => {
		return sum + (w.amount || 0);
	}, 0);

	cash_close_info.orders.forEach((order) => {
		let amount_paid = getAmountPaidForOrder(payment_info_list, order.id);

		total_sales += order.total;
		total_order_discount -= order.discount;
		total_order_amount_paid += amount_paid;
		total_credit += (order.total - order.discount) - amount_paid;
	});

	cash_close_info.item_sales.forEach((x) => {
		if (x.type != 'REFUND' && x.is_free_of_charge != 'YES') {
			item_total += x.total;
			total_item_discount += x.item_discount;
		}
	});

	let total_funds = cash_close_info.funds.reduce((prev: number, fund: Fund) => {
		return prev + fund.amount;
	}, 0);

	let expenses_excluded_types = ['CREDIT_CARD', 'DEBIT_CARD', 'TRANSFER', 'CHECK', 'DIGITAL_WALLET', 'PAYPAL', 'COUPON'];

	cash_close_info.movements.forEach((bm: Bank_Movement) => {
		if (bm.type == 'income') {
			let key = INCOME_LEGEND[bm.transaction_type + '-' + bm.currency_id];

			if (!(key in movements_qtys)) {
				movements_qtys[key] = { total: 0, currency_id: bm.currency_id, equivalencia: 0 };
			}

			if (movements_qtys[key].currency_id == 'USD') {
				let total = bm.total * currency_rate.rate;

				movements_qtys[key].equivalencia += total;
				movements_qtys[key].total += bm.total;
				currencies_total += total;
			}
			else {
				movements_qtys[key].equivalencia += bm.total;
				movements_qtys[key].total += bm.total;
				currencies_total += bm.total;
			}
		}
		else {
			let key = EXPENSE_LEGEND[bm.transaction_type] || bm.transaction_type;

			if (!(key in expense_movements_qtys)) {
				expense_movements_qtys[key] = { total: 0, currency_id: bm.currency_id, equivalencia: 0 };
			}

			expense_movements_qtys[key].total += bm.total;
			expense_movements_qtys[key].equivalencia += bm.total;

			if (!expenses_excluded_types.includes(bm.transaction_type)) {
				total_expenses += bm.total;
			}
		}
	});

	let total_in_caja =
		total_income_sales
		+ total_income_other
		+ total_funds
		- total_expenses
		- total_withdrawals
		- (movements_qtys['Tarjeta Crédito'].total || 0)
		- (movements_qtys['Tarjeta Débito'].total || 0)
		- (movements_qtys['Transferencia'].total || 0)
		- (movements_qtys['Cheque'].total || 0)
		- (movements_qtys['Monedero']?.total || 0)
		- total_returns;

	let diferencia_caja = cash_close_info.cash_close.cash_on_hand - total_in_caja;

	let show_negative_warning = total_returns > (
		total_income_sales + total_income_other + total_funds - total_expenses
		- (movements_qtys['Tarjeta Crédito']?.total || 0)
		- (movements_qtys['Tarjeta Débito']?.total || 0)
		- (movements_qtys['Transferencia']?.total || 0)
		- (movements_qtys['Cheque']?.total || 0)
		- (movements_qtys['Monedero']?.total || 0) - total_returns);

	return {
		total_sales,
		total_credit,
		total_order_discount,
		total_item_discount,
		item_total,
		currencies_total,
		total_income_sales,
		total_income_other,
		total_order_amount_paid,
		total_funds,
		total_expenses,
		total_withdrawals,
		withdrawals,
		expenses_paymens,
		movements_qtys,
		expense_movements_qtys,
		currency_rate,
		total_returns,
		total_change_amount,
		payments_with_change,
		cash_count_mxn_bills,
		cash_count_usd_bills,
		cash_count_mxn_coins,
		cash_count_usd_coins,
		total_in_caja,
		diferencia_caja,
		show_negative_warning
	};
}

export function toCashCloseTicketData(inputs: CashCloseReportInputs, totals: CashCloseComputedTotals): CashCloseTicketData {
	return {
		store: inputs.store,
		billing_data: inputs.billing_data,
		user_name: inputs.cash_close_info?.user?.name || '',
		current_date: inputs.current_date,
		start: inputs.cash_close_info.cash_close.start,
		end: inputs.cash_close_info.cash_close.end,
		note: inputs.cash_close_info.cash_close.note,
		total_sales: totals.total_sales,
		total_credit: totals.total_credit,
		total_order_discount: totals.total_order_discount,
		total_item_discount: totals.total_item_discount,
		total_income_sales: totals.total_income_sales,
		total_income_other: totals.total_income_other,
		total_expenses: totals.total_expenses,
		total_expenses_all: sumExpenseMovements(totals.expense_movements_qtys),
		total_withdrawals: totals.total_withdrawals,
		total_returns: totals.total_returns,
		total_funds: totals.total_funds,
		total_in_caja: totals.total_in_caja,
		cash_on_hand: inputs.cash_close_info.cash_close.cash_on_hand,
		diferencia_caja: totals.diferencia_caja,
		currencies_total: totals.currencies_total,
		usd_rate: totals.currency_rate.rate,
		funds: inputs.cash_close_info.funds,
		withdrawals: totals.withdrawals,
		expense_movements_qtys: totals.expense_movements_qtys,
		movements_qtys: totals.movements_qtys,
		references: groupCashCountReferences(inputs.cash_close_info.cash_counts),
		mxn_bills: totals.cash_count_mxn_bills,
		usd_bills: totals.cash_count_usd_bills,
		mxn_coins: totals.cash_count_mxn_coins,
		usd_coins: totals.cash_count_usd_coins,
		show_negative_warning: totals.show_negative_warning
	};
}

@Injectable({
	providedIn: 'root'
})
export class CashCloseReportService {

	constructor(private rest: RestService) {
	}

	getReport(cash_close_id: number | string): Observable<CashCloseReport> {
		return this.rest.cash_close_info.get(cash_close_id).pipe(
			mergeMap((cash_close_info: CashCloseInfo) => this.fetchInputs(cash_close_info)),
			map((inputs: CashCloseReportInputs) => {
				const totals = computeCashCloseTotals(inputs);
				return {
					inputs,
					totals,
					ticket_data: toCashCloseTicketData(inputs, totals)
				};
			})
		);
	}

	private fetchInputs(cash_close_info: CashCloseInfo): Observable<CashCloseReportInputs> {
		let d = Utils.getDateFromUTCMysqlString(cash_close_info.cash_close.since);

		let payment_info_search = {
			eq: { created_by_user_id: cash_close_info.cash_close.created_by_user_id, status: 'ACTIVE' as ('ACTIVE' | 'DELETED') },
			ge: { created: d },
			le: { created: cash_close_info.cash_close.created },
			limit: 99999999,
			search_extra: {}
		};

		if (this.rest.has_ares) {
			payment_info_search.search_extra['ares'] = 1;
		}

		let payment_obs = this.rest.payment_info.search(payment_info_search);

		let bill_data_obs = cash_close_info.store?.default_billing_data_id
			? this.rest.billing_data.get(cash_close_info.store.default_billing_data_id)
			: of(null);

		let order_ids = cash_close_info.orders && cash_close_info.orders.length > 0
			? cash_close_info.orders.map(order => order.id)
			: [];

		let returnsSearch: Partial<SearchObject<ReturnsRequest>> = {
			csv: {
				order_id: order_ids
			}
		};

		let obs_returns = order_ids.length > 0
			? this.rest.returns_info.searchAll(returnsSearch, 50, true)
			: of({ total: 0, data: [] } as RestResponse<ReturnsInfo>);

		return forkJoin({
			payment_info: payment_obs,
			default_billing_data: bill_data_obs,
			returns: obs_returns
		}).pipe(
			map((response) => ({
				cash_close_info,
				payment_info_list: response.payment_info.data,
				returns_info_list: response.returns.data,
				billing_data: response.default_billing_data,
				store: cash_close_info.store,
				current_date: new Date()
			} as CashCloseReportInputs))
		);
	}
}
